/**
 * Loading/fetch decisions for the side-panel RelationshipRecordForm (the modal
 * that edits an object_template_attribute, role_permission, user_git_public_key,
 * …). Extracted as pure functions so the regression below is unit-testable
 * without rendering the component or standing up the redux store.
 *
 * REGRESSION GUARD (Stefan, 2026-10-20 — operator-task-fe-ot-attribute-modal-
 * description-not-loaded-cleared-on-edit): the edit form MUST render from a
 * COMPLETE single-resource fetch, never from a partially-populated cache entry.
 *
 * The single-resource redux slice (state.resources[iri]) is written both by the
 * full single fetch (STORE_RESOURCE_DATA) AND by partial merges
 * (STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE — e.g. the live GraphQL subscription),
 * so a PARTIAL record (carrying only the columns in the list/subscription body —
 * name, datatype — but not description / is_hidden / silent_update / is_nullable)
 * can land there first. The old gate triggered the full fetch only when NO cached
 * data existed at all (`isOpen && !resourceData`), so a partial cache entry
 * short-circuited the fetch: the form rendered the missing fields as empty /
 * default, and saving then diffed those real stored values down to "" / false,
 * silently clearing them.
 *
 * The fix: for an existing record, ALWAYS perform one full fetch per open and
 * keep the form hidden until it resolves — the presence (or partialness) of a
 * cached record is deliberately irrelevant to this decision, which is why these
 * predicates do not take `resourceData` as an input.
 */

export interface RelationshipFormLoadState {
    /** Whether the side-panel form is currently open. */
    isOpen: boolean;
    /** The id of the record being edited; falsy for a brand-new record. */
    recordId?: string | null;
    /** Whether a full single-resource fetch has completed for this open. */
    hasLoadedFull: boolean;
}

/**
 * A full single-resource fetch should be (re)triggered when the form is open for
 * an EXISTING record and that full fetch has not yet completed this open.
 * Deliberately does not consider any cached `resourceData` — a partial cache
 * entry must NOT suppress the fetch (that was the clear-on-edit bug).
 */
export const needsFullResourceFetch = ({
    isOpen,
    recordId,
    hasLoadedFull,
}: RelationshipFormLoadState): boolean => isOpen && !!recordId && !hasLoadedFull;

/**
 * The form should show the loading placeholder (rather than the editable fields)
 * while an existing record's full data has not yet loaded. A new record
 * (no recordId) has nothing to fetch and is never in this loading state.
 */
export const isRelationshipFormLoading = ({
    recordId,
    hasLoadedFull,
}: Pick<RelationshipFormLoadState, "recordId" | "hasLoadedFull">): boolean =>
    !!recordId && !hasLoadedFull;
